// Shapes returned by the /flow routes, plus the role vocabulary and small formatters.

export type FlowRole = 'engineer' | 'tech_lead' | 'qa' | 'pm' | 'devops' | 'design';

export const FLOW_ROLES: { key: FlowRole; label: string; tone: string }[] = [
  { key: 'engineer', label: 'Engineering', tone: 'bg-blue-50 text-blue-700 ring-blue-200' },
  { key: 'tech_lead', label: 'Tech Lead', tone: 'bg-indigo-50 text-indigo-700 ring-indigo-200' },
  { key: 'qa', label: 'QA', tone: 'bg-emerald-50 text-emerald-700 ring-emerald-200' },
  { key: 'pm', label: 'Product', tone: 'bg-violet-50 text-violet-700 ring-violet-200' },
  { key: 'devops', label: 'DevOps', tone: 'bg-amber-50 text-amber-800 ring-amber-200' },
  { key: 'design', label: 'Design', tone: 'bg-pink-50 text-pink-700 ring-pink-200' },
];

export const roleLabel = (key: string): string => FLOW_ROLES.find((r) => r.key === key)?.label ?? key;
export const roleTone = (key: string): string => FLOW_ROLES.find((r) => r.key === key)?.tone ?? 'bg-gray-50 text-gray-600 ring-gray-200';

export interface FlowTotals {
  moves: number;
  checked: number;
  outside: number;
  adherence_pct: number | null;
  rework: number;
  rework_rate_pct: number | null;
  unchecked?: { unmapped: number; shared: number; no_role: number };
}

export interface FlowProjectRow extends FlowTotals {
  project_key: string;
  project_name: string | null;
}

export interface FlowSummary {
  period: string;
  overall: FlowTotals;
  projects: FlowProjectRow[];
}

export interface HandoffRow {
  id: string;
  issue_key: string;
  issue_name: string | null;
  issue_type: string | null;
  project_key: string;
  jira_url: string;
  at: string;
  from: string;
  to: string;
  actor: { account_id: string; name: string | null; roles: FlowRole[] };
  owners: string[];
  hint: string;
}

export interface HandoffPerson {
  account_id: string;
  name: string | null;
  roles: FlowRole[];
  moves: number;
  checked: number;
  outside: number;
  adherence_pct: number | null;
}

export interface ReworkData {
  period: string;
  moves: number;
  rework: number;
  rework_rate_pct: number | null;
  by_pair: { from: string; to: string; kind: 'bounce' | 'back'; count: number }[];
  by_project: { project_key: string; project_name: string | null; moves: number; rework: number; rework_rate_pct: number | null }[];
  tickets: { issue_key: string; issue_name: string | null; project_key: string; jira_url: string; count: number }[];
}

export interface WaitingStatus {
  status: string;
  position: number | null;
  stays: number;
  pass_through: number;
  median_hours: number | null;
  p90_hours: number | null;
}

export interface WaitingData {
  period: string;
  statuses: WaitingStatus[];
  stuck: {
    total: number;
    recent: number;
    stale: number;
    by_project: { project_key: string; project_name: string | null; count: number; recent: number }[];
    by_status: { status: string; position: number | null; threshold_days: number; count: number; over_90: number; over_365: number }[];
  };
}

export interface StuckRow {
  issue_key: string;
  issue_name: string | null;
  issue_type: string | null;
  project_key: string;
  jira_url: string;
  status: string;
  days: number;
  threshold_days: number;
  since: string;
  assignee: { account_id: string; name: string | null } | null;
}
